import { useMemo } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  MarkerType,
  type Node,
  type Edge,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import { STAGES, RETURN_LABEL, ACTOR_COLOR } from "./diagramData";
import { StageNode } from "./StageNode";
import { FloatingEdge } from "./FloatingEdge";
import { ServiceChip } from "../ServiceChip";
import { ACTOR_LABEL } from "./diagramData";
import { useMediaQuery } from "../../lib/hooks";

const nodeTypes = { stage: StageNode };
const edgeTypes = { floating: FloatingEdge };

// Racetrack layout — two rows of three big cards that fill the panel, flowing
// 1→2→3 across the top, down the right, 4←5←6 back across the bottom, and up
// the left (the return edge). COL/ROW = center-to-center spacing; tuned so the
// bounding box roughly matches the panel aspect and the cards nearly fill it.
const COL = 440;
const ROW = 360;

// top-left positions, indexed by STAGES order (intake, process, progress,
// pay, deliver, pickup): top row L→R, bottom row R→L.
const POS = [
  { x: 0, y: 0 }, // 1 intake      top-left
  { x: COL, y: 0 }, // 2 process     top-mid
  { x: 2 * COL, y: 0 }, // 3 in progress top-right
  { x: 2 * COL, y: ROW }, // 4 ready → pay  bottom-right
  { x: COL, y: ROW }, // 5 deliver     bottom-mid
  { x: 0, y: ROW }, // 6 pickup      bottom-left
];

const RETURN_EDGE_LABEL = "↺ 40% RETURN · 3+ TIMES";

function buildGraph(): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = STAGES.map((stage, i) => {
    return {
      id: stage.id,
      type: "stage",
      position: POS[i],
      data: { stage },
      draggable: false,
    };
  });

  const edges: Edge[] = STAGES.map((stage, i) => {
    const isReturn = i === STAGES.length - 1;
    const next = STAGES[(i + 1) % STAGES.length];
    return {
      id: `${stage.id}->${next.id}`,
      source: stage.id,
      target: next.id,
      type: "floating",
      animated: true,
      data: isReturn ? { label: RETURN_EDGE_LABEL } : undefined,
      style: {
        stroke: "var(--amber)",
        strokeWidth: 2,
        opacity: isReturn ? 0.95 : 0.5,
        strokeDasharray: isReturn ? "6 5" : undefined,
      },
      markerEnd: { type: MarkerType.ArrowClosed, color: "var(--amber)", width: 16, height: 16 },
    };
  });

  return { nodes, edges };
}

function DiagramCanvas() {
  const { nodes, edges } = useMemo(buildGraph, []);
  return (
    <div className="screen h-[560px] w-full overflow-hidden">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        fitViewOptions={{ padding: 0.06 }}
        minZoom={0.2}
        maxZoom={1.5}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag={false}
        zoomOnScroll={false}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
        preventScrolling={false}
        proOptions={{ hideAttribution: false }}
      >
        <Background variant={BackgroundVariant.Dots} gap={22} size={1} color="var(--line)" />
      </ReactFlow>
    </div>
  );
}

/** Mobile / reduced fallback: a stacked list of stages (no pan/zoom canvas). */
function StageList() {
  return (
    <div className="screen flex flex-col gap-2 p-3">
      {STAGES.map((stage) => {
        const accent = ACTOR_COLOR[stage.actor];
        return (
          <div
            key={stage.id}
            className="panel p-3.5"
            style={{ borderLeftColor: accent, borderLeftWidth: 2 }}
          >
            <div className="flex items-center gap-2.5">
              <span
                className="flex h-7 w-7 flex-none items-center justify-center rounded-full border font-mono text-base"
                style={{ borderColor: accent, color: accent }}
              >
                {stage.n}
              </span>
              <div className="etch-label etch !text-[13px] !text-text">{stage.name}</div>
              <span
                className="ml-auto font-mono text-[11px] uppercase tracking-[0.1em]"
                style={{ color: accent }}
              >
                {ACTOR_LABEL[stage.actor]}
              </span>
            </div>
            <p className="mt-2 text-[13.5px] leading-[1.5] text-text-dim">{stage.action}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {stage.services.map((s) => (
                <ServiceChip key={s} app={s} />
              ))}
            </div>
          </div>
        );
      })}
      <div className="etch-label glow mt-1 self-center rounded px-2.5 py-1 !text-amber">
        {RETURN_LABEL}
      </div>
    </div>
  );
}

export function FilmLabDiagram() {
  // React Flow's pan/zoom canvas is awkward on small screens — fall back to a
  // stacked list there.
  const isNarrow = useMediaQuery("(max-width: 820px)");
  if (isNarrow) return <StageList />;
  return (
    <ReactFlowProvider>
      <DiagramCanvas />
    </ReactFlowProvider>
  );
}
